import { useEffect } from "react";
import { HomesPriceQuote, IPerson, Person } from "redmond";

/**
 * Persists the main guest whenever the payload actually changes, rather than only
 * when a new price quote lands.
 *
 * Keying on the price quote alone missed two cases: editing a traveler (the PUT
 * mutates in place, so the id and the quote are both unchanged) and changing the
 * confirmation email. Both left a stale guest on the booking.
 */
export const usePersistMainGuest = ({
  priceQuote,
  email,
  phoneNumber,
  userSelectedTraveler,
  addMainGuest,
  priceQuoteInProgress,
  travelerInfoCommitted,
}: {
  priceQuote: HomesPriceQuote | null;
  email?: string;
  phoneNumber?: string;
  userSelectedTraveler?: IPerson;
  addMainGuest: (args: { emailAddress: string; mainGuest: Person | null }) => void;
  // Skip while a quote is (re)scheduling — its orderedProductId is about to be
  // replaced; the fresh quote re-runs this effect against the right one.
  priceQuoteInProgress?: boolean;
  // Only persist once the step is committed (Save & continue on desktop, past the
  // contact step on mobile), so editing email/traveler beforehand doesn't fire.
  travelerInfoCommitted?: boolean;
}) => {
  const orderedProductId = priceQuote?.orderedProductId;

  const payloadKey =
    orderedProductId && email && userSelectedTraveler
      ? JSON.stringify({
          orderedProductId,
          email,
          phoneNumber,
          traveler: userSelectedTraveler,
        })
      : null;

  useEffect(() => {
    if (
      !travelerInfoCommitted ||
      priceQuoteInProgress ||
      !payloadKey ||
      !email ||
      !userSelectedTraveler
    ) {
      return;
    }
    addMainGuest({
      emailAddress: email,
      mainGuest: { ...userSelectedTraveler, phoneNumber },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payloadKey encodes every value the request depends on
  }, [payloadKey]);
};
